import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon, Body2, InsertLinkIcon } from "@activecollab/components";
import PropTypes from "prop-types";
import { CardOrCallToUpgradeCard } from "~/components/CardOrCallToUpgradeCard/CardOrCallToUpgradeCard";
import Angie from "~/utils/angie";

export const IntegrationCard = ({
  features,
  context,
  url,
  img,
  title,
  description,
  hasFeature,
  integration,
  finished,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logIntegrationClick = useCallback(async () => {
    if (integration) {
      await Angie.logger.info(
        `User opened ${integration.name} integration settings`,
        {
          integration_name: integration.name,
          already_in_use: integration.is_in_use,
        },
        true
      );
      if (!url) {
        return;
      }
      if (finished) {
        navigate(url);
      } else {
        window.open(url, "_self", "noreferrer");
      }
    }
  }, [finished, integration, navigate, url]);

  const resolveLabel = useMemo(() => {
    const shortName = integration?.short_name;
    const isInUse = integration?.is_in_use;
    if (shortName === "slack" && isInUse) {
      const channels = integration?.already_connected[0]?.notification_channels;
      if (channels) {
        return `${channels.length} ${t("Connected")}`;
      }
    }

    if (shortName === "webhooks" && integration?.is_in_use) {
      const webhooks = integration?.already_connected[0]?.webhooks;
      if (webhooks) {
        return `${webhooks.length} ${t("Connected")}`;
      }
    }

    return t("Connected");
  }, [integration, t]);

  const resolveRightPart = useMemo(() => {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
        {integration?.is_in_use ? (
          <>
            <InsertLinkIcon />
            <Body2>{resolveLabel}</Body2>
          </>
        ) : (
          <ArrowRightIcon />
        )}
      </div>
    );
  }, [integration, resolveLabel]);
  return (
    <CardOrCallToUpgradeCard
      hasFeature={!!hasFeature}
      features={features}
      context={context}
      page="AppsAndIntegrations"
      rightSide={resolveRightPart}
      onCardClick={logIntegrationClick}
    >
      <div className="tw-flex tw-flex-col tw-flex-1 tw-truncate tw-pr-3">
        <div className="tw-flex tw-gap-4 tw-items-center">
          {img ? (
            <img
              src={img}
              alt={title}
              width={32}
              height={32}
            />
          ) : null}
          <div>
            <Body2
              className="tw-mb-0.5"
              weight="bold"
            >
              {title}
            </Body2>
            <Body2 color="tertiary">{description}</Body2>
          </div>
        </div>
      </div>
    </CardOrCallToUpgradeCard>
  );
};

IntegrationCard.propTypes = {
  features: PropTypes.array.isRequired,
  context: PropTypes.string.isRequired,
  url: PropTypes.string,
  img: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  hasFeature: PropTypes.bool.isRequired,
  integration: PropTypes.object,
  finished: PropTypes.bool,
};
